/**
 * /r/:slug — universal influencer tracking redirector.
 *
 * Calls the backend /api/r/:slug endpoint (which records the click +
 * sets the 90-day tal_inf cookie) and then navigates the user to either
 * the marketplace home or a `?to=` override path. This must run inside
 * the React app (not as a server-side 302) because the k8s ingress only
 * proxies /api/* to the backend — root-level paths hit the SPA.
 *
 * Shows a sub-second cyan "redirecting…" splash so the user never sees
 * a flash of blank black page. Falls back gracefully to "/" if the API
 * call fails.
 */
import { useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function InfluencerRedirect() {
  const { slug } = useParams();
  const [search] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      let target = '/';
      try {
        const to = search.get('to');
        const url = `${API}/api/r/${encodeURIComponent(slug || '')}${to ? `?to=${encodeURIComponent(to)}` : ''}`;
        const res = await axios.get(url, { withCredentials: true });
        if (res.data?.redirect_to && res.data.redirect_to.startsWith('/')) {
          target = res.data.redirect_to;
        }
      } catch (_) {
        // Bad slug or network — just send them home.
      }
      if (!cancelled) navigate(target, { replace: true });
    })();
    return () => { cancelled = true; };
  }, [slug, search, navigate]);

  return (
    <div className="min-h-screen bg-black text-white flex items-center justify-center" data-testid="influencer-redirect">
      <div className="text-center">
        <div className="w-10 h-10 mx-auto mb-4 border-2 border-[#00F0FF] border-t-transparent rounded-full animate-spin" />
        <p className="text-sm text-neutral-400">Loading theailedger.ai…</p>
      </div>
    </div>
  );
}
